import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { fetchAdminMe, fetchFinancingLeads } from "@/lib/api";
import { CreditCard, Loader2, ArrowLeft, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { COUNTRY_LABELS } from "@/lib/banks";

const fmt = (n) => (typeof n === "number" ? Math.round(n).toLocaleString("fr-FR") : "—");

export default function AdminFinancingPage() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState(null);

  useEffect(() => {
    fetchAdminMe()
      .then(() => fetchFinancingLeads().then(setLeads).catch(() => toast.error("Erreur")))
      .catch(() => navigate("/", { replace: true }));
  }, [navigate]);

  if (!leads) {
    return <main className="max-w-7xl mx-auto px-6 py-20 text-center"><Loader2 className="animate-spin mx-auto text-orange-500" /></main>;
  }

  return (
    <main className="max-w-7xl mx-auto px-6 py-10" data-testid="admin-financing-page">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-orange-600 font-semibold mb-4" data-testid="back-to-admin">
        <ArrowLeft size={14} /> Tableau de bord
      </Link>
      <h1 className="font-display text-4xl font-extrabold tracking-tight inline-flex items-center gap-3">
        <CreditCard size={32} className="text-indigo-500" /> Demandes de financement
      </h1>
      <p className="text-[var(--ink-soft)] mt-2">
        {leads.length} demande{leads.length > 1 ? "s" : ""} — à transmettre aux banques partenaires.
      </p>

      {leads.length === 0 ? (
        <p className="text-sm text-[var(--ink-soft)] mt-8">Aucune demande pour le moment.</p>
      ) : (
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm border-collapse" data-testid="financing-leads-table">
            <thead>
              <tr className="bg-gray-50 text-left text-xs">
                {["Date", "Client", "Contact", "Montant", "Banque / Pays", "Mensualité", "Réf annonce"].map((h) => (
                  <th key={h} className="border border-[var(--line)] px-3 py-2 font-bold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} data-testid={`financing-lead-${l.id}`}>
                  <td className="border border-[var(--line)] px-3 py-2 text-xs text-[var(--ink-soft)]">{(l.created_at || "").slice(0, 10)}</td>
                  <td className="border border-[var(--line)] px-3 py-2 font-semibold">{l.name}</td>
                  <td className="border border-[var(--line)] px-3 py-2 text-xs">
                    <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1 text-orange-600"><Phone size={11} /> {l.phone}</a>
                    {l.email && <div className="inline-flex items-center gap-1 text-[var(--ink-soft)] mt-0.5"><Mail size={11} /> {l.email}</div>}
                  </td>
                  <td className="border border-[var(--line)] px-3 py-2 font-bold text-orange-600">{fmt(l.amount)} F</td>
                  <td className="border border-[var(--line)] px-3 py-2 text-xs">
                    {l.bank || "—"}<div className="text-[var(--ink-soft)]">{COUNTRY_LABELS[l.country] || l.country || ""}</div>
                  </td>
                  <td className="border border-[var(--line)] px-3 py-2 text-xs">{fmt(l.monthly)} F/mois</td>
                  <td className="border border-[var(--line)] px-3 py-2 text-xs">{l.article_ref || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
